// "Set your status" — the manual half of Availability. Reached from the user menu
// in the header and from a conversation's Details panel; one component, one flow.
//
// Two rules the UI must respect, because the server already does:
//  · the options are the SERVER's list (never a hardcoded set here);
//  · a status derived from approved leave is not the person's to clear — the dialog
//    explains that instead of offering a Clear button that would do nothing.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { buildTenantPath, extractTenantSlug } from '@/features/navigation/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import {
  AVAILABILITY_NOTE_MAX,
  buildAvailabilityPayload,
  canClearAvailability,
  durationLabel,
  isLeaveDerived,
} from '../availability';
import { useMyAvailability } from '../hooks/useAvailability';
import { AvailabilityBadge, AvailabilityDot } from './AvailabilityBadge';
import type { AvailabilityOption } from '../types';

/** The overrides offered next to an option's own suggestion. 0 = rest of today. */
const DURATION_CHOICES = [15, 30, 60, 120, 0] as const;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const StatusDialog: React.FC<Props> = ({ open, onOpenChange }) => {
  const { t } = useTranslation('conversations');
  const location = useLocation();
  const timeOffPath = buildTenantPath('time-off', extractTenantSlug(location.pathname));
  const { availability, options, note, loading, saving, save, clear } = useMyAvailability(open);

  const [picked, setPicked] = useState<string>('');
  // null = "use the suggestion that comes with the picked status".
  const [minutes, setMinutes] = useState<number | null>(null);
  const [text, setText] = useState('');

  // Open on what the person currently has: their status preselected, their note in
  // the box — editing a status, not starting from blank.
  useEffect(() => {
    if (!open) return;
    setPicked(availability && availability.source === 'manual' ? String(availability.kind) : '');
    setText(availability?.source === 'manual' ? availability.text ?? '' : '');
    setMinutes(null);
  }, [open, availability?.kind, availability?.source, availability?.text]);

  const durationTexts = useMemo(
    () => ({
      restOfToday: t('availability_rest_of_today', { defaultValue: 'the rest of today' }),
      minutes: (n: number) => t('availability_minutes', { defaultValue: '{{count}} minutes', count: n }),
      hour: t('availability_hour', { defaultValue: '1 hour' }),
      hours: (n: number) => t('availability_hours', { defaultValue: '{{count}} hours', count: n }),
    }),
    [t],
  );

  const option = options.find((o) => o.kind === picked);
  const leaveDerived = isLeaveDerived(availability);
  const showClear = canClearAvailability(availability);

  const submit = async () => {
    if (!option || saving) return;
    try {
      await save(buildAvailabilityPayload(option, { minutes, note: text }));
      toast.success(t('availability_updated', { defaultValue: 'Status updated' }));
      onOpenChange(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('availability_failed', { defaultValue: "Couldn't update your status" })));
    }
  };

  const drop = async () => {
    if (saving) return;
    try {
      await clear();
      setPicked('');
      setText('');
      toast.success(t('availability_cleared', { defaultValue: 'Status cleared' }));
      onOpenChange(false);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('availability_failed', { defaultValue: "Couldn't update your status" })));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="status-dialog">
        <DialogHeader>
          <DialogTitle>{t('availability_title', { defaultValue: 'Set your status' })}</DialogTitle>
          {note && <DialogDescription data-testid="status-server-note">{note}</DialogDescription>}
        </DialogHeader>

        {/* What you look like to colleagues right now. */}
        {availability && (
          <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2">
            <AvailabilityDot availability={availability} ring={false} />
            <span className="min-w-0 flex-1 truncate text-sm text-charcoal">
              {availability.label}
              {availability.detail ? ` · ${availability.detail}` : ''}
            </span>
            <AvailabilityBadge availability={availability} compact />
          </div>
        )}

        {/* A leave-derived status is the leave record's to change, not this dialog's. */}
        {leaveDerived && (
          <p data-testid="status-leave-locked" className="rounded-lg bg-info-soft px-3 py-2 text-xs text-charcoal">
            {t('availability_leave_locked', {
              defaultValue:
                'This comes from approved time off. Only the leave record changes it — clearing it here is not possible.',
            })}
          </p>
        )}

        {loading && options.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('availability_loading', { defaultValue: 'Loading…' })}</p>
        ) : (
          <>
            <ul className="max-h-64 space-y-1 overflow-y-auto">
              {options.map((o: AvailabilityOption) => (
                <li key={o.kind}>
                  <button
                    type="button"
                    data-testid={`status-option-${o.kind}`}
                    aria-pressed={picked === o.kind}
                    onClick={() => setPicked(o.kind)}
                    className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                      picked === o.kind
                        ? 'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-500/[0.12]'
                        : 'border-border text-charcoal hover:bg-muted'
                    }`}
                  >
                    <span aria-hidden className="text-base">{o.emoji}</span>
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    <span className="flex-none text-xs text-muted-foreground">
                      {t('availability_for', {
                        defaultValue: 'for {{duration}}',
                        duration: durationLabel(o.default_minutes, durationTexts),
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>

            {/* Picking a leave-ish status must never read as booking leave. */}
            {option?.leave_hint && (
              <p data-testid="status-leave-hint" className="text-xs text-muted-foreground">
                {t('availability_leave_hint', { defaultValue: 'This does not book time off.' })}{' '}
                <Link
                  to={timeOffPath}
                  onClick={() => onOpenChange(false)}
                  className="font-medium text-primary-600 hover:underline"
                >
                  {t('availability_request_time_off', { defaultValue: 'Request time off' })}
                </Link>
              </p>
            )}

            <label className="block text-xs text-muted-foreground">
              {t('availability_note_label', { defaultValue: 'Add a note (optional)' })}
              <input
                value={text}
                maxLength={AVAILABILITY_NOTE_MAX}
                onChange={(e) => setText(e.target.value.slice(0, AVAILABILITY_NOTE_MAX))}
                placeholder={t('availability_note_placeholder', { defaultValue: 'Back after lunch' })}
                className="mt-1 w-full rounded border border-border bg-transparent px-2 py-1 text-sm text-charcoal"
              />
              <span className="mt-0.5 block text-right text-[11px] text-muted-foreground">
                {t('availability_note_counter', {
                  defaultValue: '{{current}} / {{max}}',
                  current: text.length,
                  max: AVAILABILITY_NOTE_MAX,
                })}
              </span>
            </label>

            <label className="block text-xs text-muted-foreground">
              {t('availability_duration_label', { defaultValue: 'Clear after' })}
              <select
                value={minutes === null ? '' : String(minutes)}
                onChange={(e) => setMinutes(e.target.value === '' ? null : Number(e.target.value))}
                className="mt-1 w-full rounded border border-border bg-transparent px-2 py-1 text-sm text-charcoal"
              >
                <option value="">
                  {option
                    ? t('availability_duration_default', {
                        defaultValue: 'Suggested ({{duration}})',
                        duration: durationLabel(option.default_minutes, durationTexts),
                      })
                    : t('availability_duration_default_empty', { defaultValue: 'Suggested' })}
                </option>
                {DURATION_CHOICES.map((m) => (
                  <option key={m} value={m}>
                    {durationLabel(m, durationTexts)}
                  </option>
                ))}
              </select>
            </label>

            <div className="flex items-center justify-between gap-2">
              {showClear ? (
                <button
                  type="button"
                  data-testid="status-clear"
                  disabled={saving}
                  onClick={drop}
                  className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50"
                >
                  {t('availability_clear', { defaultValue: 'Clear status' })}
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                data-testid="status-save"
                disabled={!option || saving}
                onClick={submit}
                className="rounded-lg bg-primary-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-primary-600 disabled:opacity-50"
              >
                {saving
                  ? t('availability_saving', { defaultValue: 'Saving…' })
                  : t('availability_save', { defaultValue: 'Save status' })}
              </button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StatusDialog;
